/**
 * Offline data adapter.
 *
 * Replays the baked snapshot (src/lib/baked.json) through the same query
 * surface that queries.ts exposes for live mode. The dashboard components
 * don't know which mode they're in — they call functions; this file
 * decides whether to hit Cube REST or return baked rows.
 *
 * The implementation is intentionally minimal — sufficient for the offline
 * single-file build, but not a general SQL engine. We aggregate in-memory
 * with plain JS reducers.
 */

import baked from './baked.json' with { type: 'json' };
import type { CityRow, FilterOption, KpiTotals, ProvinceRow, SkuInCity } from './queries.js';
import { store } from './stores.svelte.js';

interface BakedProduct {
  'dim_product.sku_id':       number;
  'dim_product.description':  string;
  'dim_product.brand':        string;
  'dim_product.principal':    string;
  'dim_product.product_type': string;
}
interface BakedCity {
  'view_city_metrics.city_megastore_id':       number;
  'view_city_metrics.psgc_code':               string;
  'view_city_metrics.city':                    string;
  'view_city_metrics.province':                string;
  'view_city_metrics.region':                  string;
  'view_city_metrics.mp_score':                number;
  'view_city_metrics.consumer_profile_primary': string;
}
interface BakedCatSig {
  'view_category_signals.product_type':       string;
  'view_category_signals.city_megastore_id':  number;
  'view_category_signals.cat_gmv_8w':         number;
  'view_category_signals.cat_slope':          number;
}
interface BakedReg {
  'view_regression_v1_1.sku_id':            number;
  'view_regression_v1_1.city_megastore_id': number;
  'view_regression_v1_1.gpiv_tier':         string;
  'view_regression_v1_1.my_slope':          number;
  'view_regression_v1_1.my_gmv_8w':         number;
}

const products = baked.products as unknown as BakedProduct[];
const cities   = baked.cities   as unknown as BakedCity[];
const catSigs  = baked.catSignals as unknown as BakedCatSig[];
const regs     = baked.regression as unknown as BakedReg[];

/* ── Index by SKU and city ─────────────────────────────────────────────── */
const productById = new Map(products.map((p) => [p['dim_product.sku_id'], p]));
const cityById    = new Map(cities.map((c)   => [c['view_city_metrics.city_megastore_id'], c]));
const catKey      = (pt: string, c: number) => `${pt}|${c}`;
const catByKey    = new Map(catSigs.map((s) => [
  catKey(s['view_category_signals.product_type'], s['view_category_signals.city_megastore_id']),
  s,
]));

function matchesProductFilter(p: BakedProduct | undefined): boolean {
  if (!p) return false;
  if (store.principal   && p['dim_product.principal']    !== store.principal)   return false;
  if (store.productType && p['dim_product.product_type'] !== store.productType) return false;
  if (store.brand       && p['dim_product.brand']        !== store.brand)       return false;
  if (store.sku_id !== null && p['dim_product.sku_id']   !== store.sku_id)      return false;
  return true;
}

export function offline_fetchPeriods(): Promise<string[]> {
  const p = baked.meta?.period;
  return Promise.resolve(p ? [p] : []);
}

export function offline_fetchPrincipals(): Promise<FilterOption[]> {
  const counts = new Map<string, number>();
  for (const p of products) {
    const k = p['dim_product.principal'];
    if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return Promise.resolve(
    [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count),
  );
}

export function offline_fetchProductTypes(principal: string): Promise<FilterOption[]> {
  if (!principal) return Promise.resolve([]);
  const counts = new Map<string, number>();
  for (const p of products) {
    if (p['dim_product.principal'] !== principal) continue;
    const k = p['dim_product.product_type'];
    if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return Promise.resolve([...counts.entries()].map(([value, count]) => ({ value, count })));
}

export function offline_fetchBrands(principal: string, productType: string): Promise<FilterOption[]> {
  if (!principal) return Promise.resolve([]);
  const counts = new Map<string, number>();
  for (const p of products) {
    if (p['dim_product.principal'] !== principal) continue;
    if (productType && p['dim_product.product_type'] !== productType) continue;
    const k = p['dim_product.brand'];
    if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return Promise.resolve([...counts.entries()].map(([value, count]) => ({ value, count })));
}

export function offline_fetchSkus(brand: string): Promise<{ sku_id: number; description: string }[]> {
  if (!brand) return Promise.resolve([]);
  return Promise.resolve(
    products
      .filter((p) => p['dim_product.brand'] === brand)
      .map((p) => ({ sku_id: p['dim_product.sku_id'], description: p['dim_product.description'] }))
      .sort((a, b) => a.sku_id - b.sku_id),
  );
}

export function offline_fetchKpiTotals(): Promise<KpiTotals> {
  let myGmv = 0, catGmv = 0, sop = 0, count = 0;
  const seenCat = new Set<string>();

  for (const r of regs) {
    const p = productById.get(r['view_regression_v1_1.sku_id']);
    if (!matchesProductFilter(p)) continue;
    myGmv += r['view_regression_v1_1.my_gmv_8w'];
    count++;
    const pt = p!['dim_product.product_type'];
    const k  = catKey(pt, r['view_regression_v1_1.city_megastore_id']);
    if (!seenCat.has(k)) {
      const cs = catByKey.get(k);
      if (cs) {
        catGmv += cs['view_category_signals.cat_gmv_8w'];
        const slope = cs['view_category_signals.cat_slope'];
        sop += cs['view_category_signals.cat_gmv_8w'] * Math.max(slope, 0);
      }
      seenCat.add(k);
    }
  }
  const share = catGmv > 0 ? myGmv / catGmv : 0;
  // Apply share dampener consistent with Cube SoP formula
  sop = sop * (1 - Math.min(share, 1) * 0.85);
  return Promise.resolve({ my_gmv: myGmv, cat_gmv: catGmv, sop, share, cities: count });
}

export function offline_fetchCityRows(): Promise<CityRow[]> {
  const out: CityRow[] = [];
  for (const r of regs) {
    const p = productById.get(r['view_regression_v1_1.sku_id']);
    if (!matchesProductFilter(p)) continue;
    const c = cityById.get(r['view_regression_v1_1.city_megastore_id']);
    if (!c) continue;
    const cs = catByKey.get(catKey(p!['dim_product.product_type'], c['view_city_metrics.city_megastore_id']));
    const catGmv   = cs?.['view_category_signals.cat_gmv_8w'] ?? 0;
    const catSlope = cs?.['view_category_signals.cat_slope']   ?? 0;
    const myGmv    = r['view_regression_v1_1.my_gmv_8w'];
    const share    = catGmv > 0 ? myGmv / catGmv : 0;
    const sop      = catGmv * Math.max(catSlope, 0) * (1 - Math.min(share, 1) * 0.85);
    out.push({
      city_id:   c['view_city_metrics.city_megastore_id'],
      psgc:      c['view_city_metrics.psgc_code'],
      city:      c['view_city_metrics.city'],
      province:  c['view_city_metrics.province'],
      region:    c['view_city_metrics.region'],
      brand:     p!['dim_product.brand'],
      sku_id:    store.sku_id !== null ? p!['dim_product.sku_id'] : null,
      sku:       store.sku_id !== null ? p!['dim_product.description'] : '',
      gpiv:      r['view_regression_v1_1.my_slope'],
      gpiv_tier: r['view_regression_v1_1.gpiv_tier'],
      my_gmv:    myGmv,
      cat_gmv:   catGmv,
      sop,
      share,
      mp:        c['view_city_metrics.mp_score'],
      consumer_profile: c['view_city_metrics.consumer_profile_primary'],
    });
  }
  out.sort((a, b) => b.sop - a.sop);
  return Promise.resolve(out.slice(0, 2000));
}

export function offline_fetchProvinces(): Promise<ProvinceRow[]> {
  const agg = new Map<string, ProvinceRow>();
  for (const r of regs) {
    const p = productById.get(r['view_regression_v1_1.sku_id']);
    if (!matchesProductFilter(p)) continue;
    const c = cityById.get(r['view_regression_v1_1.city_megastore_id']);
    if (!c) continue;
    const cs = catByKey.get(catKey(p!['dim_product.product_type'], c['view_city_metrics.city_megastore_id']));
    const catGmv   = cs?.['view_category_signals.cat_gmv_8w'] ?? 0;
    const catSlope = cs?.['view_category_signals.cat_slope']   ?? 0;
    const myGmv    = r['view_regression_v1_1.my_gmv_8w'];
    const share    = catGmv > 0 ? myGmv / catGmv : 0;
    const sop      = catGmv * Math.max(catSlope, 0) * (1 - Math.min(share, 1) * 0.85);
    const prov = c['view_city_metrics.province'];
    const e = agg.get(prov) ?? { province: prov, cities: 0, my_gmv: 0, sop: 0 };
    e.cities++;
    e.my_gmv += myGmv;
    e.sop    += sop;
    agg.set(prov, e);
  }
  return Promise.resolve([...agg.values()].sort((a, b) => b.sop - a.sop));
}

export function offline_fetchCityDiagnostic(cityId: number): Promise<SkuInCity[]> {
  const out: SkuInCity[] = [];
  for (const r of regs) {
    if (r['view_regression_v1_1.city_megastore_id'] !== cityId) continue;
    const p = productById.get(r['view_regression_v1_1.sku_id']);
    if (!matchesProductFilter(p)) continue;
    const cs = catByKey.get(catKey(p!['dim_product.product_type'], cityId));
    const catGmv   = cs?.['view_category_signals.cat_gmv_8w'] ?? 0;
    const catSlope = cs?.['view_category_signals.cat_slope']   ?? 0;
    const myGmv    = r['view_regression_v1_1.my_gmv_8w'];
    const share    = catGmv > 0 ? myGmv / catGmv : 0;
    out.push({
      sku_id:    p!['dim_product.sku_id'],
      sku:       p!['dim_product.description'],
      brand:     p!['dim_product.brand'],
      gpiv:      r['view_regression_v1_1.my_slope'],
      gpiv_tier: r['view_regression_v1_1.gpiv_tier'],
      my_gmv:    myGmv,
      cat_gmv:   catGmv,
      sop:       catGmv * Math.max(catSlope, 0) * (1 - Math.min(share, 1) * 0.85),
      share,
    });
  }
  out.sort((a, b) => b.sop - a.sop);
  return Promise.resolve(out);
}
